/**
 * MessageBubble — shared chat message renderer.
 *
 * Owner-spec 2026-05-31 (Phase 1 of 4): the chat UI was duplicated between
 * WhatsAppChat.js (full omnichannel view) and AgentStatusManager.js's
 * ChatModal (the bottom-right popup). The full view had a partial media
 * renderer; the modal rendered only `msg.body` as text — so `[image]` and
 * `pdf.210` placeholders leaked through even after the backend started
 * persisting media correctly.
 *
 * This component is the single source of truth for one message bubble:
 *   - alignment (fromMe vs incoming)
 *   - sender chip
 *   - media renderer per type (image / video / audio / voice / document)
 *   - WhatsApp-style loading + broken states for any media URL
 *   - timestamp + ack indicators
 *
 * Lightbox state lives in the PARENT (each chat view owns its own); this
 * component just calls `onMediaClick(msg)` so the parent can open the
 * lightbox at the right index.
 *
 * Tolerant of absolute mediaUrl (legacy entries from before PR #607).
 * Doesn't assume same-origin: any URL works.
 *
 * Action menu (Phase 3) + Reply quote (Phase 2) + Reactions (Phase 4) will
 * layer in on top of this component without re-touching the two callers.
 */

import React, { useState } from 'react';

const formatBytes = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const formatDuration = (sec) => {
  if (!sec || isNaN(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const fileIconFor = (mime, filename) => {
  const m = (mime || '').toLowerCase();
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (m.includes('pdf') || ext === 'pdf') return '📕';
  if (m.includes('word') || ['doc', 'docx'].includes(ext)) return '📘';
  if (m.includes('sheet') || m.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) return '📗';
  if (m.includes('zip') || ['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  if (m.startsWith('image/')) return '🖼️';
  return '📄';
};

// ─── Loading + broken states ──────────────────────────────────────────────

const MediaLoadingPlaceholder = ({ aspect = '4 / 3', label = 'טוען...' }) => (
  <div style={{
    width: '260px',
    aspectRatio: aspect,
    background: 'linear-gradient(135deg, #e7e7e7, #f3f3f3)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#888',
    fontSize: '12px',
  }}>
    {label}
  </div>
);

const MediaBrokenState = ({ url, label = 'הקובץ לא נטען' }) => (
  <div style={{
    width: '260px',
    padding: '12px',
    background: '#fff1f0',
    border: '1px dashed #ffa39e',
    borderRadius: '8px',
    color: '#a8071a',
    fontSize: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  }}>
    <div>⚠️ {label}</div>
    {url && (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1565c0', fontSize: '11px', wordBreak: 'break-all' }}
      >
        פתח בכרטיסייה חדשה
      </a>
    )}
  </div>
);

// ─── Image ────────────────────────────────────────────────────────────────

const ImageMessage = ({ msg, onClick }) => {
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const url = msg.mediaUrl || msg.body;

  return (
    <div style={{ position: 'relative', maxWidth: '320px' }}>
      {status === 'loading' && <MediaLoadingPlaceholder />}
      {status === 'error' && <MediaBrokenState url={url} label="טעינת תמונה נכשלה" />}
      {/*
        2026-06-22 fix: `loading="lazy"` combined with `display:none` made the
        browser skip the fetch entirely (a hidden image has no layout, so the
        lazy-load IntersectionObserver never decides it's "near viewport").
        Result: onLoad never fired, status stayed 'loading', spinner stuck
        forever. Fix is to keep the <img> in the DOM with layout (so the
        fetch happens) while it overlays the placeholder via position:absolute,
        and is hidden only by visibility/opacity. Removed `loading="lazy"` —
        chat windows render only the visible messages so the perf gain of
        lazy was small, and the bug it caused was severe.
      */}
      <img
        src={url}
        alt={msg.caption || 'תמונה'}
        onLoad={() => setStatus('ok')}
        onError={() => setStatus('error')}
        onClick={() => status === 'ok' && onClick && onClick(msg)}
        style={{
          // 2026-09-03 — owner: "the image should appear at full size, not
          // enlarged". `width: '100%'` STRETCHED a small picture to the full
          // 320px bubble, past its own resolution and into blur, and
          // `objectFit: 'cover'` then CROPPED whatever no longer fitted the
          // 320x320 box — a receipt photographed narrow lost its edges.
          // `auto` + `contain` let the picture keep its own size and its own
          // proportions; the max-* rules stay and are now the only limit, so
          // nothing is scaled UP and nothing is cut off.
          // The same fix is applied to the Workspace's own renderer
          // (workspace/styles/workspace.css :: .ws-msg__img) — this tree and
          // that one draw images with different code.
          width: 'auto',
          maxWidth: '320px',
          maxHeight: '320px',
          objectFit: 'contain',
          borderRadius: '8px',
          cursor: status === 'ok' ? 'zoom-in' : 'default',
          // Keep the <img> in flow so the browser fetches it. While
          // loading, hide it but don't remove it from the DOM.
          ...(status === 'ok'
            ? { display: 'block' }
            : { position: 'absolute', top: 0, left: 0, opacity: 0, pointerEvents: 'none' }),
        }}
      />
      {status === 'ok' && msg.caption && (
        <div style={{ marginTop: '6px', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
          {msg.caption}
        </div>
      )}
    </div>
  );
};

// ─── Video ────────────────────────────────────────────────────────────────

const VideoMessage = ({ msg }) => {
  const [status, setStatus] = useState('loading');
  const url = msg.mediaUrl || msg.body;
  return (
    <div style={{ maxWidth: '320px' }}>
      {status === 'loading' && <MediaLoadingPlaceholder aspect="16 / 9" label="טוען וידאו..." />}
      {status === 'error' && <MediaBrokenState url={url} label="טעינת וידאו נכשלה" />}
      <video
        controls
        preload="metadata"
        src={url}
        onLoadedMetadata={() => setStatus('ok')}
        onError={() => setStatus('error')}
        style={{
          width: '100%',
          maxWidth: '320px',
          borderRadius: '8px',
          display: status === 'ok' ? 'block' : 'none',
        }}
      />
      {status === 'ok' && msg.caption && (
        <div style={{ marginTop: '6px', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
          {msg.caption}
        </div>
      )}
    </div>
  );
};

// ─── Audio / Voice ────────────────────────────────────────────────────────

const AudioMessage = ({ msg, isVoice }) => {
  const [status, setStatus] = useState('loading');
  const url = msg.mediaUrl || msg.body;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '6px 8px',
      minWidth: '240px',
    }}>
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: isVoice ? '#25D366' : '#5b9bd5',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        flexShrink: 0,
      }}>
        {isVoice ? '🎤' : '🎵'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {status === 'error' ? (
          <MediaBrokenState url={url} label={isVoice ? 'טעינת הקלטה נכשלה' : 'טעינת אודיו נכשלה'} />
        ) : (
          <audio
            controls
            preload="metadata"
            src={url}
            onLoadedMetadata={() => setStatus('ok')}
            onError={() => setStatus('error')}
            style={{ width: '100%', height: '36px' }}
          />
        )}
      </div>
    </div>
  );
};

// ─── Document (PDF / generic) ─────────────────────────────────────────────

const DocumentMessage = ({ msg }) => {
  const url = msg.mediaUrl || msg.body;
  const filename = msg.mediaFilename || (msg.fileName) || 'קובץ';
  const size = formatBytes(msg.mediaSize);
  const icon = fileIconFor(msg.mediaMimeType, filename);
  const isPdf = (msg.mediaMimeType || '').includes('pdf') || filename.toLowerCase().endsWith('.pdf');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '10px',
      background: '#f6f6f6',
      borderRadius: '8px',
      minWidth: '240px',
      maxWidth: '320px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ fontSize: '28px', lineHeight: 1 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#1a1a1a',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {filename}
          </div>
          <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>
            {isPdf ? 'PDF' : (msg.mediaMimeType || 'קובץ')}{size && ` · ${size}`}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            textAlign: 'center',
            background: '#25D366',
            color: 'white',
            padding: '6px',
            borderRadius: '6px',
            fontSize: '12px',
            textDecoration: 'none',
            fontWeight: 500,
          }}
        >
          פתח
        </a>
        <a
          href={url}
          download={filename}
          style={{
            flex: 1,
            textAlign: 'center',
            background: 'white',
            color: '#25D366',
            border: '1px solid #25D366',
            padding: '6px',
            borderRadius: '6px',
            fontSize: '12px',
            textDecoration: 'none',
            fontWeight: 500,
          }}
        >
          הורד
        </a>
      </div>
      {msg.caption && (
        <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{msg.caption}</div>
      )}
    </div>
  );
};

// ─── Media dispatch ───────────────────────────────────────────────────────

/**
 * Render just the media body of a message (no bubble chrome).
 * Exported so existing callers can adopt the media renderer
 * incrementally without committing to the full MessageBubble wrapper.
 */
export const renderMedia = (msg, onMediaClick) => {
  const t = (msg.mediaType || msg.type || '').toLowerCase();
  if (t === 'image') return <ImageMessage msg={msg} onClick={onMediaClick} />;
  if (t === 'video') return <VideoMessage msg={msg} />;
  if (t === 'audio') return <AudioMessage msg={msg} isVoice={false} />;
  if (t === 'voice') return <AudioMessage msg={msg} isVoice={true} />;
  if (t === 'document') return <DocumentMessage msg={msg} />;
  return null;
};

// ─── Bubble ───────────────────────────────────────────────────────────────

/**
 * Props:
 *   msg              — the message object (body, type, mediaType, mediaUrl, …)
 *   onMediaClick     — (msg) => void; called on image click → parent opens lightbox
 *   variant          — 'modal' | 'panel'; controls max-width + spacing.
 *                      Default 'panel'.
 *   showSender       — boolean. Show msg.sentByName above outgoing msgs.
 */
const MessageBubble = ({ msg, onMediaClick, variant = 'panel', showSender = true }) => {
  const fromMe = !!msg.fromMe;
  const media = renderMedia(msg, onMediaClick);
  const hasText = msg.body && !media; // media renderer handles its own caption

  // Bubble alignment in WhatsApp uses 'right' for outgoing for LTR; this app
  // is RTL so we mirror — agent (fromMe) bubbles render left, customer right.
  // Keeps consistency with the existing two callers.
  const bubbleStyle = {
    background: fromMe ? '#dcf8c6' : 'white',
    padding: '8px 12px',
    borderRadius: '8px',
    maxWidth: variant === 'modal' ? '85%' : '70%',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    wordBreak: 'break-word',
  };

  const ts = msg.timestamp
    ? new Date(typeof msg.timestamp === 'number' && msg.timestamp < 1e12
        ? msg.timestamp * 1000
        : msg.timestamp)
    : null;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: fromMe ? 'flex-start' : 'flex-end',
        marginBottom: '8px',
      }}
    >
      <div style={bubbleStyle}>
        {fromMe && showSender && msg.sentByName && (
          <div style={{
            fontSize: '11px',
            color: '#128c7e',
            fontWeight: 600,
            marginBottom: '4px',
          }}>
            {msg.sentByName}
          </div>
        )}
        {media}
        {hasText && (
          <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
            {msg.body}
          </div>
        )}
        <div style={{
          fontSize: '10px',
          color: '#999',
          marginTop: '4px',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          justifyContent: 'flex-end',
        }}>
          {ts && (
            <span>
              {ts.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {fromMe && <span>{msg.ack >= 2 ? '✓✓' : '✓'}</span>}
        </div>
      </div>
    </div>
  );
};

/**
 * Build a `replyTarget` snapshot from a chat message — used by the
 * three chat consumers when the agent triggers Reply on an existing
 * bubble. The snapshot is the same shape the BE persists under
 * `replyTo` on outbound entries (Phase 2 owner-spec 2026-05-31), so it
 * can be serialised verbatim into the send payload.
 */
export const buildReplyTarget = (msg) => {
  if (!msg) return null;
  return {
    id: msg.id,
    body: msg.body || '',
    type: (msg.mediaType || msg.type || 'text').toLowerCase(),
    mediaType: (msg.mediaType || '').toLowerCase(),
    mediaUrl: msg.mediaUrl || '',
    senderName: msg.sentByName || (msg.fromMe ? 'נציג' : 'לקוח'),
    fromMe: !!msg.fromMe,
  };
};

/**
 * Helper to build the lightbox media list from a messages array. Filter
 * to image/video, preserve chronological order, return [{url, type,
 * caption, ts, filename}]. Parent passes this to <MediaLightbox> +
 * tracks currentIndex.
 */
export const buildMediaList = (messages) => {
  return (messages || [])
    .filter(m => {
      const t = (m.mediaType || m.type || '').toLowerCase();
      return (t === 'image' || t === 'video') && (m.mediaUrl || m.body);
    })
    .map(m => ({
      id: m.id,
      url: m.mediaUrl || m.body,
      absoluteUrl: m.mediaUrlAbsolute || m.mediaUrl || m.body,
      type: (m.mediaType || m.type || '').toLowerCase(),
      caption: m.caption || '',
      filename: m.mediaFilename || '',
      ts: m.timestamp,
    }));
};

export default MessageBubble;
